import { useState, useMemo, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    Settings, MessageSquare, List, Gift, ArrowUp, ArrowDown, CreditCard, Upload, Download, ShoppingCart,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthProvider';
import TabNav from '../../../components/TabNav/TabNav';
import BotSelector from '../../../components/BotSelector/BotSelector';
import EditBot from '../Edit/EditBot';
import OrderBump from '../OrderBump/OrderBump';
import Downsell from '../Downsell/Downsell';
import Upsell from '../Upsell/Upsell';
import Buttons from '../Buttons/Buttons';
import Welcome from '../Welcome/Welcome';
import Plans from '../Plans/Plans';
import Webhooks from '../Webhooks/Webhooks';
import PixConfig from '../PixConfig/PixConfig';
import CheckoutConfig from '../CheckoutConfig/CheckoutConfig';
import WebhookIcon from '../../../components/Icons/WebhookIcon';
import { BotHeaderSkeleton, TabSkeleton } from './BotManagementSkeleton';
import { getVisibleBots } from '../../../utils/bots';
import { authService } from '../../../services/api';
import ExportConfigModal from '../../../components/Modal/ExportConfigModal';
import ImportConfigModal from '../../../components/Modal/ImportConfigModal';
import BotProfileModal from '../../../components/Modal/BotProfileModal';
import BotIcon from '../../../components/Icons/BotIcon';
import CurrencyModeToggle from '../../../components/CurrencyModeToggle/CurrencyModeToggle';

export default function BotManagement() {
    const { botId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isLoading, loadUser } = useAuth();

    // Get user bots (visíveis e ordenados)
    const bots = getVisibleBots(user?.bots);

    // Find selected bot
    const ownBot = useMemo(() => {
        if (!botId || bots.length === 0) return null;
        return bots.find(b => String(b.id) === String(botId)) || null;
    }, [botId, bots]);

    // Admin abrindo bot de OUTRO dono (atalho do painel admin): o bot não está
    // em user.bots, então busca pela API (o backend já autoriza admin). As abas
    // internas carregam tudo pelo botId da URL.
    const [adminBot, setAdminBot] = useState(null);
    const [adminBotFailed, setAdminBotFailed] = useState(false);
    useEffect(() => {
        setAdminBot(null);
        setAdminBotFailed(false);
        if (!user?.is_admin || !botId || isLoading || ownBot) return;
        let cancelled = false;
        authService.bots.edit_bot(botId)
            .then(({ data }) => {
                if (cancelled) return;
                if (data?.bot) setAdminBot(data.bot); else setAdminBotFailed(true);
            })
            .catch(() => { if (!cancelled) setAdminBotFailed(true); });
        return () => { cancelled = true; };
    }, [user?.is_admin, botId, isLoading, ownBot]);

    // Bot que não é do usuário (e ele não é admin), ou que não existe: em vez de
    // erro, cai na edição do bot padrão DELE (último selecionado, senão o primeiro).
    const redirectToOwn = !isLoading && !!user && !!botId && !ownBot && bots.length > 0
        && (!user.is_admin || adminBotFailed);
    useEffect(() => {
        if (!redirectToOwn) return;
        let savedId = null;
        try { savedId = localStorage.getItem('selectedBotId'); } catch { }
        const fallback = bots.find(b => String(b.id) === String(savedId)) || bots[0];
        navigate(`/bots/manage/${fallback.id}?tab=edit`, { replace: true });
    }, [redirectToOwn]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedBot = ownBot || adminBot;
    const isForeignBot = !ownBot && !!adminBot;
    // O seletor lista os bots do admin; o bot alheio entra no topo para aparecer.
    const selectorBots = isForeignBot ? [adminBot, ...bots] : bots;

    // Sempre que um bot PRÓPRIO estiver selecionado aqui, salva como "último selecionado"
    useEffect(() => {
        if (ownBot) {
            try {
                localStorage.setItem('selectedBotId', ownBot.id);
            } catch { }
        }
    }, [ownBot]);

    // Detecta qual aba deve estar ativa baseado na URL ou query string
    const getInitialTab = () => {
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');
        if (tabParam) return tabParam;

        // Detecta baseado no path para manter compatibilidade com URLs antigas
        if (location.pathname.includes('/edit/')) return 'edit';
        if (location.pathname.includes('/order_bump/')) return 'order_bump';
        if (location.pathname.includes('/downsell/')) return 'downsell';

        return 'edit';
    };

    const [activeTab, setActiveTab] = useState(getInitialTab());

    useEffect(() => {
        setBotPhotoUrl(null);
        if (selectedBot?.bot_photo_file_id) {
            authService.bots.get_bot_photo(selectedBot.id)
                .then(({ data }) => setBotPhotoUrl(data.photo_url || null))
                .catch(() => setBotPhotoUrl(null));
        }
    }, [selectedBot?.id]);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [botPhotoUrl, setBotPhotoUrl] = useState(null);

    const tabs = [
        { id: 'edit', label: 'Editar Bot', icon: <Settings size={16} /> },
        { id: 'welcome', label: 'Boas-vindas', icon: <MessageSquare size={16} /> },
        { id: 'plans', label: 'Planos', icon: <List size={16} /> },
        { id: 'order_bump', label: 'Order Bump', icon: <Gift size={16} /> },
        { id: 'upsell', label: 'Upsell', icon: <ArrowUp size={16} /> },
        { id: 'downsell', label: 'Downsell', icon: <ArrowDown size={16} /> },
        // { id: 'webhooks', label: 'Webhooks', icon: <WebhookIcon /> },
        // 'buttons' foi movido para dentro de Boas-vindas (bloco "Botões de redirect").
        { id: 'pix', label: 'Pagamentos', icon: <CreditCard size={16} /> },
        { id: 'checkout', label: 'Checkout', icon: <ShoppingCart size={16} /> },
    ];

    const handleBotSelect = (bot) => {
        // Navigate to the new bot with the current active tab
        navigate(`/bots/manage/${bot.id}?tab=${activeTab}`);
    };

    // Troca de aba: além do estado, grava a aba na URL (?tab=) para que um refresh
    // NÃO caia sempre em "Editar Bot" — a aba atual é restaurada de getInitialTab.
    const handleTabChange = (id) => {
        setActiveTab(id);
        const sp = new URLSearchParams(location.search);
        sp.set('tab', id);
        navigate(`${location.pathname}?${sp.toString()}`, { replace: true });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Data desconhecida';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getBotStatus = (bot) => {
        if (!bot) return { label: 'Nenhum bot selecionado', badgeClass: 'bg-gray-700 text-gray-200' };
        if (!bot.is_active) return { label: 'Inativo', badgeClass: 'bg-gray-800 text-gray-200' };
        if (bot.is_running) return { label: 'Rodando', badgeClass: 'bg-emerald-600 text-white' };
        return { label: 'Parado', badgeClass: 'bg-yellow-600 text-white' };
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'edit':
                return <EditBot />;
            case 'welcome':
                return <Welcome />;
            case 'plans':
                return <Plans />;
            case 'pix':
                return <PixConfig />;
            case 'checkout':
                return <CheckoutConfig />;
            case 'buttons':
                return <Buttons />;
            // case 'webhooks':
            //     return <Webhooks />;
            case 'order_bump':
                return <OrderBump />;
            case 'downsell':
                return <Downsell />;
            case 'upsell':
                return <Upsell />;
            default:
                return <EditBot />;
        }
    };

    const status = getBotStatus(selectedBot);

    return (
        <div className="page">
            <header className="mb-6">


                {isLoading || redirectToOwn ? (
                    <BotHeaderSkeleton tabs={tabs.length} />
                ) : selectedBot ? (
                    <div className='spt-bot-header w-full mt-4 spt-reveal'>
                        {isForeignBot && (
                            <div className="mx-4 mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/25 text-xs text-amber-300">
                                <i className="fas fa-user-shield" />
                                Modo admin: você está editando um bot de outro usuário. As alterações valem para o bot do dono.
                            </div>
                        )}
                        <div className="mt-4 w-full rounded-lg      py-0 px-4">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">


                                <div className="flex items-center gap-3 flex-wrap">
                                    <div className="w-[280px] max-w-xs">
                                        <BotSelector
                                            bots={selectorBots}
                                            selectedBot={selectedBot}
                                            onSelect={handleBotSelect}
                                            allBotsOption={false}
                                        />
                                    </div>

                                    {selectedBot?.id && (
                                        <CurrencyModeToggle key={selectedBot.id} botId={selectedBot.id} />
                                    )}

                                </div>


                                <div className="flex flex-col items-start md:items-end gap-1 text-xs text-gray-300">

                                <div className="flex items-center gap-2">
                                    <button title="Exportar Config Key" className="btn-secondary btn-action" onClick={() => setShowExportModal(true)}>
                                        <Download size={16} />
                                    </button>
                                    <button title="Importar Config Key" className="btn-primary btn-action" onClick={() => setShowImportModal(true)}>
                                        <Upload size={16} />
                                    </button>
                                                                        <button className='btn-primary btn-action sm:hidden'
                                        type="button"
                                        onClick={() => setShowProfileModal(true)}
                                    >
                                        <Settings size={16} />
                                    </button>
                                </div>

                                    <span>
                                        Criado em: {formatDate(selectedBot.created_at)}
                                        {` às ${formatTime(selectedBot.created_at)}`}
                                    </span>
                                    <span>
                                        Ativo:{' '}
                                        <strong className={selectedBot.is_active ? 'text-emerald-400' : 'text-red-400'}>
                                            {selectedBot.is_active ? 'Sim' : 'Não'}
                                        </strong>
                                    </span>

                                    <span>
                                        Rodando agora:{' '}
                                        <strong className={selectedBot.is_running ? 'text-emerald-400' : 'text-red-400'}>
                                            {selectedBot.is_running ? 'Sim' : 'Não'}
                                        </strong>
                                    </span>


                                </div>
                            </div>
                        </div>
                        <div className="mt-4 lg:border-t lg:border-white/10">
                            <TabNav
                                tabs={tabs}
                                activeTab={activeTab}
                                onTabChange={handleTabChange}
                                noMarginBottom
                            />
                        </div>
                    </div>
                ) : (
                    <div className="mt-4">
                        <TabNav
                            tabs={tabs}
                            activeTab={activeTab}
                            onTabChange={handleTabChange}
                        />
                    </div>
                )}
            </header>

            {/* Enquanto o cabeçalho carrega (ou redireciona), a aba NÃO monta: ela
                terminaria antes e apareceria pronta sob um cabeçalho ainda em
                skeleton. No lugar, o skeleton da própria aba — quando ela monta,
                o skeleton dela continua do mesmo formato. Redirecionando, montar
                buscaria o bot alheio e mostraria erro. */}
            {isLoading || redirectToOwn ? <TabSkeleton tab={activeTab} delayed /> : renderContent()}

            <ExportConfigModal
                open={showExportModal}
                bot={selectedBot}
                onClose={() => setShowExportModal(false)}
            />

            <ImportConfigModal
                open={showImportModal}
                bots={bots}
                initialBot={selectedBot}
                onClose={() => setShowImportModal(false)}
                onImported={async (importedBotId) => {
                    // Recarrega o usuário (planos/config do bot destino podem ter
                    // mudado) e vai direto pro bot que RECEBEU a config — que pode
                    // ser diferente do bot sendo visualizado agora, já que o modal
                    // deixa escolher o destino livremente.
                    await loadUser();
                    navigate(`/bots/manage/${importedBotId}?tab=${activeTab}`);
                }}
            />

            <BotProfileModal
                open={showProfileModal}
                bot={selectedBot}
                onClose={() => setShowProfileModal(false)}
                onUpdated={(updated) => {
                    if ('bot_photo_file_id' in updated) {
                        if (updated.bot_photo_file_id) {
                            authService.bots.get_bot_photo(selectedBot.id)
                                .then(({ data }) => setBotPhotoUrl(data.photo_url || null))
                                .catch(() => setBotPhotoUrl(null));
                        } else {
                            setBotPhotoUrl(null);
                        }
                    }
                }}
            />
        </div>
    );
}
